import * as React from 'react';
import moment from 'moment';
import { shallowEqual } from 'react-redux';
import { Container, Table } from 'react-bootstrap';

import { RejectToggle } from '../../../components/RejectToggle';

import { useAppSelector } from '../../../redux/store';
import { OscillometryMeasurement } from '../../../Types/Common/MeasurementTypes';
import { GroupedOscillometryMeasurement } from './OscillometryDetailedView';
import { Program } from '../../../Types/Common/Types';

const { useState } = React;

interface OscillometryDetailedTableViewProps {
  groupedMeasurement: GroupedOscillometryMeasurement;
  toggleRejected: (waveform: OscillometryMeasurement) => Promise<void>;
  isReadOnly: boolean | undefined;
  program: Program;
}

export const OscillometryDetailedTableView: React.FC<OscillometryDetailedTableViewProps> = ({
  groupedMeasurement,
  toggleRejected,
  isReadOnly,
  program
}) => {
  const translationText = useAppSelector(state => state.translations, shallowEqual);
  // Track loading state per waveform measurementID
  const [loadingMeasurementID, setLoadingMeasurementID] = useState<string | null>(null);
  const seriesEditing = program?.featureSet?.enableSeriesEditing;

  const bestMeasurements = groupedMeasurement.bestMeasurements;
  const waveforms = groupedMeasurement.waveforms;

  const handleToggleRejected = async (waveform: OscillometryMeasurement) => {
    if (isReadOnly || loadingMeasurementID) return;
    setLoadingMeasurementID(waveform.measurementID);
    try {
      await toggleRejected(waveform);
    } finally {
      setLoadingMeasurementID(null);
    }
  };

  return (
    <Container className="effort-table-view">
      <Table size="sm" responsive borderless className="OscillometryTabledMeasurements">
        <thead>
          <tr>
            <th>Measurement number</th>
            <th>{translationText.time || 'Time'}</th>
            <th>R5 ({bestMeasurements?.R5?.unit})</th>
            <th>R5-20 ({bestMeasurements?.R5_20?.unit})</th>
            <th>AX ({bestMeasurements?.AX?.unit})</th>
            <th>VT ({bestMeasurements?.VT?.unit})</th>
            <th>COH5</th>
            <th>Fres ({bestMeasurements?.Fres?.unit})</th>
            <th>Respiratory Rate ({bestMeasurements?.respiratoryRate?.unit})</th>
            <th>{translationText.status || 'Status'}</th>
          </tr>
        </thead>
        <tbody>
          {waveforms.length > 0 ? (
            waveforms.map((waveform, index) => {
              const isAccepted = waveform.rejected !== undefined && !waveform.rejected;
              const measurementNum = waveforms.length - index;
              const isThisLoading = loadingMeasurementID === waveform.measurementID;

              return (
                <tr key={waveform.measurementID} className={!isAccepted ? 'text-muted' : ''}>
                  <td className="font-weight-bold">
                    # {measurementNum}
                  </td>
                  <td>{moment.parseZone(waveform.roundtripTime).format('h:mm:ss a')}</td>
                  <td>
                    {waveform.R5?.value?.toFixed(2) ?? '--'}
                  </td>
                  <td>
                    {waveform.R5_20?.value?.toFixed(2) ?? '--'}
                  </td>
                  <td>
                    {waveform.AX?.value?.toFixed(2) ?? '--'}
                  </td>
                  <td>
                    {waveform.VT?.value?.toFixed(2) ?? '--'}
                  </td>
                  <td>
                    {waveform.COH5?.value?.toFixed(2) ?? '--'}
                  </td>
                  <td>
                    {waveform.Fres?.value?.toFixed(2) ?? '--'}
                  </td>
                  <td>
                    {waveform.respiratoryRate?.value?.toFixed(2) ?? '--'}
                  </td>
                  <td>
                    <RejectToggle
                      rejected={!!waveform.rejected}
                      handleChange={() => handleToggleRejected(waveform)}
                      statsText={translationText}
                      isLoading={isThisLoading}
                      disabled={!seriesEditing}
                    />
                  </td>
                </tr>
              );
            })
          ) : (
            <tr>
              <td colSpan={9} className="text-center text-muted py-3">
                {translationText.noMeasurementsFound || 'No measurements found'}
              </td>
            </tr>
          )}
        </tbody>
      </Table>
    </Container>
  );
};